import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Quagga from "quagga";
import { CheckCircle, Plus, ArrowUpFromLine, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Validate EAN code (8, 12, 13, or 14 digits)
export const isValidEan = (code) => {
  if (!code) return false;
  const cleaned = code.trim();
  if (!/^\d+$/.test(cleaned)) return false;
  if (![8, 12, 13, 14].includes(cleaned.length)) return false;
  return true;
};

export function useScanPage(user, stocktakingHandlers = {}) {
  const { startStocktaking, finishStocktaking } = stocktakingHandlers;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  
  // Track if URL EAN lookup has been done (prevents double lookup in Strict Mode)
  const urlEanProcessed = useRef(false);
  
  // Core state
  const [ean, setEan] = useState("");
  const [lagerplats, setLagerplats] = useState(searchParams.get('location') || "");
  const [antal, setAntal] = useState(1);
  const [processing, setProcessing] = useState(false);
  const [lastScanned, setLastScanned] = useState(null);
  const [locations, setLocations] = useState([]);
  const [recentScans, setRecentScans] = useState([]);
  const [recentLocations, setRecentLocations] = useState(() => {
    // Load recent locations from localStorage
    try {
      const saved = localStorage.getItem('recent_scan_locations');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  
  // Inventory mode
  const [inventoryMode, setInventoryMode] = useState(!!searchParams.get('location'));
  const inventoryLocation = searchParams.get('location') || '';
  const [inventoryCompleted, setInventoryCompleted] = useState(false);
  const [completedLocation, setCompletedLocation] = useState("");
  const [locationProducts, setLocationProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  // Live inventering panel: products currently counted at the location (DB truth,
  // re-fetched after every scan so staff SEE that each scan is saved).
  const [inventoryList, setInventoryList] = useState([]);
  const [inventoryListLoading, setInventoryListLoading] = useState(false);
  const [lastInventoryUpdate, setLastInventoryUpdate] = useState(null); // {ean, artikelnummer, newQty, delta}
  
  // Delivery type dialog states (Fas 3)
  const [showDeliveryTypeDialog, setShowDeliveryTypeDialog] = useState(false);
  const [showZeroStockDialog, setShowZeroStockDialog] = useState(false);
  const [showBookingBlockDialog, setShowBookingBlockDialog] = useState(false);
  const [blockedBookings, setBlockedBookings] = useState([]);
  const [currentStockForDelivery, setCurrentStockForDelivery] = useState(0);
  const [bookedQuantityForDelivery, setBookedQuantityForDelivery] = useState(0);
  const [pendingDeliveryData, setPendingDeliveryData] = useState(null);
  
  // Product lookup
  const [scannedProduct, setScannedProduct] = useState(null);
  const [productLocations, setProductLocations] = useState([]);
  const [lookingUp, setLookingUp] = useState(false);
  
  // Camera state
  const [showCameraScanner, setShowCameraScanner] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [cameraLoading, setCameraLoading] = useState(false);
  const scannerRef = useRef(null);
  const scannerContainerId = "camera-scanner-container";
  
  // New product dialog
  const [showNewProductDialog, setShowNewProductDialog] = useState(false);
  const [newProduct, setNewProduct] = useState({
    ean: "",
    artikelnummer: "",
    tillverkare: "",
    kategori: "",
  });
  const [categories, setCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  
  // Scan action dialog
  const [showScanActionDialog, setShowScanActionDialog] = useState(false);
  const [scanMode, setScanMode] = useState("in");
  
  // Booking dialog for scan-out (shown after successful scan-out)
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [productBookings, setProductBookings] = useState([]);
  const [pendingProduct, setPendingProduct] = useState(null);
  const [pendingLocations, setPendingLocations] = useState([]);
  
  // Refs
  const eanInputRef = useRef(null);
  const locationInputRef = useRef(null);
  
  // Get EAN from URL params (for pre-filling from product page)
  const urlEan = searchParams.get('ean');

  // Initialize
  useEffect(() => {
    // Load all initial data in parallel
    Promise.all([
      fetchLocations(),
      fetchFilters(),
      fetchRecentScans()
    ]);
    if (inventoryMode && inventoryLocation) {
      setLagerplats(inventoryLocation);
      // Notify global stocktaking context
      if (startStocktaking) {
        startStocktaking(inventoryLocation);
      }
      // Load the live count list for this location
      refreshInventoryList(inventoryLocation);
    }
    
    // Auto-lookup if EAN provided in URL (only once)
    if (urlEan && !inventoryMode && !urlEanProcessed.current) {
      urlEanProcessed.current = true;
      setEan(urlEan);
      // Delay lookup slightly to ensure state is set
      setTimeout(() => {
        handleEanLookup(urlEan);
      }, 100);
    } else {
      eanInputRef.current?.focus();
    }
  }, []);

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      try {
        Quagga.stop();
        Quagga.offDetected();
      } catch (e) {}
    };
  }, []);

  // Re-fetch the live count list for the inventory location (DB truth).
  const refreshInventoryList = useCallback(async (loc) => {
    const target = loc || inventoryLocation;
    if (!target) return;
    setInventoryListLoading(true);
    try {
      const res = await axios.get(`${API}/inventory/location/${encodeURIComponent(target)}`);
      setInventoryList(res.data.items || []);
    } catch {
      /* keep previous list on error */
    } finally {
      setInventoryListLoading(false);
    }
  }, [inventoryLocation]);

  // Scanner keyboard input
  useEffect(() => {
    let buffer = "";
    let timeout = null;

    const handleKeyPress = (e) => {
      const activeElement = document.activeElement;
      const isInputFocused = activeElement?.tagName === "INPUT" || activeElement?.tagName === "TEXTAREA";
      
      if (showNewProductDialog) return;
      
      if (isInputFocused && activeElement !== eanInputRef.current && activeElement !== locationInputRef.current) {
        return;
      }

      if (e.key === "Enter") {
        if (buffer.length >= 8) {
          if (isValidEan(buffer)) {
            handleEanLookup(buffer);
          } else {
            toast.error(
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                <div>
                  <p className="font-medium">Ogiltig streckkod</p>
                  <p className="text-xs text-muted-foreground">"{buffer}" är inte en giltig EAN-kod.</p>
                </div>
              </div>,
              { duration: 5000 }
            );
          }
        }
        buffer = "";
        return;
      }

      if (e.key.length === 1) {
        buffer += e.key;
        clearTimeout(timeout);
        timeout = setTimeout(() => { buffer = ""; }, 100);
      }
    };

    window.addEventListener("keypress", handleKeyPress);
    return () => {
      window.removeEventListener("keypress", handleKeyPress);
      clearTimeout(timeout);
    };
  }, [showNewProductDialog]);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data);
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  };

  // Update recent locations used for scanning
  const addRecentLocation = (location) => {
    if (!location?.trim()) return;
    
    setRecentLocations(prev => {
      // Remove if already exists, then add to front
      const filtered = prev.filter(loc => loc !== location);
      const updated = [location, ...filtered].slice(0, 2); // Keep only 2 most recent
      
      // Save to localStorage
      try {
        localStorage.setItem('recent_scan_locations', JSON.stringify(updated));
      } catch (e) {
        console.error('Error saving recent locations:', e);
      }
      
      return updated;
    });
  };

  const fetchFilters = async () => {
    try {
      const [catRes, mfgRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/manufacturers`)
      ]);
      setCategories(catRes.data);
      setManufacturers(mfgRes.data);
    } catch (error) {
      console.error("Error fetching filters:", error);
    }
  };

  const fetchRecentScans = async () => {
    try {
      const response = await axios.get(`${API}/inventory/recent-scans?limit=3`);
      const scans = response.data.map(log => ({
        action: log.action_type === "OUT" ? "scanned_out" :
                log.details?.includes("Ny produkt") ? "created" : 
                log.details?.includes("Ny lagerplats") ? "created" : "updated",
        action_type: log.action_type,
        item: {
          artikelnummer: log.artikelnummer,
          ean: log.ean,
          lagerplats: log.location,
          antal: log.quantity,
          produktnamn: log.produktnamn
        },
        timestamp: log.timestamp,
        username: log.username
      }));
      setRecentScans(scans);
    } catch (error) {
      console.error("Error fetching recent scans:", error);
    }
  };

  const startCameraScanner = async () => {
    setCameraError(null);
    setCameraLoading(true);
    setShowCameraScanner(true);
    
    await new Promise(resolve => setTimeout(resolve, 300));
    
    try {
      const container = document.getElementById(scannerContainerId);
      if (!container) throw new Error("Scanner container not found");
      // Clear container safely (avoids XSS scanner false-positive on innerHTML)
      container.replaceChildren();

      Quagga.init({
        inputStream: {
          name: "Live",
          type: "LiveStream",
          target: container,
          constraints: {
            facingMode: "environment",
            width: { min: 640, ideal: 1280, max: 1920 },
            height: { min: 480, ideal: 720, max: 1080 },
          },
        },
        decoder: {
          readers: ["ean_reader", "ean_8_reader", "upc_reader", "upc_e_reader"],
          multiple: false,
        },
        locate: true,
        locator: { patchSize: "medium", halfSample: true },
      }, (err) => {
        if (err) {
          let errorMsg = "Kunde inte starta kameran";
          if (err.name === "NotAllowedError") {
            errorMsg = "Kamerabehörighet nekad.";
          } else if (err.name === "NotFoundError") {
            errorMsg = "Ingen kamera hittades.";
          }
          setCameraError(errorMsg);
          setCameraLoading(false);
          setShowCameraScanner(false);
          return;
        }
        
        Quagga.start();
        setCameraLoading(false);
        toast.info("Kamera aktiv - rikta mot streckkoden");
      });

      Quagga.onDetected((result) => {
        if (result?.codeResult?.code) {
          const code = result.codeResult.code;
          setEan(code);
          if (isValidEan(code)) {
            toast.success(`Skannad: ${code}`);
            stopCameraScanner();
            handleEanLookup(code);
          }
        }
      });

      scannerRef.current = { stop: () => Quagga.stop() };
    } catch (err) {
      setCameraError(err.message || "Kunde inte starta kameran");
      setCameraLoading(false);
      setShowCameraScanner(false);
    }
  };

  const stopCameraScanner = () => {
    try {
      Quagga.stop();
      Quagga.offDetected();
    } catch (e) {}
    scannerRef.current = null;
    setShowCameraScanner(false);
    setCameraError(null);
    setCameraLoading(false);
  };

  const handleEanLookup = async (scannedEan = null) => {
    const eanToLookup = scannedEan || ean;
    
    if (!eanToLookup.trim()) {
      toast.error("Ange EAN-kod");
      return;
    }

    if (!isValidEan(eanToLookup)) {
      toast.error(
        <div className="flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          <div>
            <p className="font-medium">Ogiltig EAN-kod</p>
            <p className="text-xs">"{eanToLookup}" är inte giltig.</p>
          </div>
        </div>,
        { duration: 5000 }
      );
      setEan("");
      eanInputRef.current?.focus();
      return;
    }

    setLookingUp(true);
    setEan(eanToLookup);
    
    try {
      const response = await axios.get(`${API}/inventory/by-ean/${eanToLookup.trim()}`);
      
      if (response.data.length > 0) {
        const product = response.data[0];
        const locationsWithStock = response.data.filter(loc => loc.antal > 0);
        
        if (locationsWithStock.length > 0 && !inventoryMode) {
          setPendingProduct({
            id: product.id,
            ean: product.ean,
            artikelnummer: product.artikelnummer,
            produktnamn: product.produktnamn,
            tillverkare: product.tillverkare,
            kategori: product.kategori,
          });
          setPendingLocations(response.data);
          setShowScanActionDialog(true);
        } else {
          setScannedProduct({
            ean: product.ean,
            artikelnummer: product.artikelnummer,
            produktnamn: product.produktnamn,
            tillverkare: product.tillverkare,
            kategori: product.kategori,
          });
          setProductLocations(response.data);
          setScanMode("in");
          toast.success(inventoryMode ? `Inventera: ${product.artikelnummer}` : `Produkt hittad: ${product.artikelnummer}`);
          if (!inventoryMode) locationInputRef.current?.focus();
        }
      } else {
        setNewProduct({
          ean: eanToLookup.trim(),
          artikelnummer: "",
          tillverkare: "",
          kategori: "",
        });
        setShowNewProductDialog(true);
        toast.info("Ny produkt - fyll i produktinfo först");
      }
    } catch (error) {
      setNewProduct({
        ean: eanToLookup.trim(),
        artikelnummer: "",
        tillverkare: "",
        kategori: "",
      });
      setShowNewProductDialog(true);
      toast.info("Ny produkt - fyll i produktinfo");
    } finally {
      setLookingUp(false);
    }
  };

  const handleScanActionChoice = async (action) => {
    setScanMode(action);
    setScannedProduct(pendingProduct);
    setProductLocations(pendingLocations);
    setShowScanActionDialog(false);
    
    toast.success(action === "in" ? `Inleverans: ${pendingProduct.artikelnummer}` : `Utleverans: ${pendingProduct.artikelnummer}`);
    
    setPendingProduct(null);
    setPendingLocations([]);
    locationInputRef.current?.focus();
  };

  const handleBookingChoice = async (booking) => {
    // Cancel the selected booking
    try {
      await axios.delete(`${API}/inventory/bookings/${booking.id}`);
      toast.success(`Bokning avbokad: ${booking.comment}`);
    } catch (error) {
      toast.error("Kunde inte avboka");
    }
    setShowBookingDialog(false);
    setProductBookings([]);
  };

  const handleSkipBooking = () => {
    setShowBookingDialog(false);
    setProductBookings([]);
  };

  const handleCancelScanAction = () => {
    setShowScanActionDialog(false);
    setPendingProduct(null);
    setPendingLocations([]);
    setEan("");
    setTimeout(() => {
      eanInputRef.current?.focus();
    }, 50);
  };

  const handleMoveChoice = () => {
    // Navigate to the product detail page with move action.
    // Use the EAN (not the per-location doc id) – the detail route resolves by EAN,
    // and the grouped-schema id from /inventory/by-ean is not a valid detail key.
    const ean = pendingProduct?.ean;
    if (ean) {
      setShowScanActionDialog(false);
      setPendingProduct(null);
      setPendingLocations([]);
      navigate(`/inventory/${ean}?action=move`);
    } else {
      toast.error("Kunde inte öppna flytt-dialog");
    }
  };

  const handleScanSubmit = async () => {
    if (!scannedProduct) {
      toast.error("Skanna en produkt först");
      eanInputRef.current?.focus();
      return;
    }
    if (!lagerplats.trim()) {
      toast.error("Ange lagerplats");
      locationInputRef.current?.focus();
      return;
    }

    setProcessing(true);
    try {
      if (scanMode === "out") {
        const locItem = productLocations.find(loc => loc.lagerplats === lagerplats);
        if (!locItem) {
          toast.error("Produkten finns inte på denna lagerplats");
          setProcessing(false);
          return;
        }
        
        // BLOCKING RULE: Check if stock is 0
        if (locItem.antal === 0) {
          setShowZeroStockDialog(true);
          setProcessing(false);
          return;
        }
        
        if (locItem.antal < antal) {
          toast.error(`Inte tillräckligt i lager. Max: ${locItem.antal} st`);
          setProcessing(false);
          return;
        }
        
        // Get bookings to check for blocked scenario
        try {
          const bookingsResponse = await axios.get(`${API}/inventory/bookings/by-ean/${scannedProduct.ean}`);
          const bookings = bookingsResponse.data || [];
          
          // Filter bookings: only location-specific bookings for THIS location block scan-out
          // Unspecified bookings (location=null) do NOT block any location
          const locationSpecificBookings = bookings.filter(b => b.location === lagerplats.trim());
          const locationSpecificBooked = locationSpecificBookings.reduce((sum, b) => sum + (b.quantity || 1), 0);
          
          // Total booked for display (all bookings)
          const totalBooked = bookings.reduce((sum, b) => sum + (b.quantity || 1), 0);
          
          // Store for delivery dialog
          setCurrentStockForDelivery(locItem.antal);
          setBookedQuantityForDelivery(totalBooked);
          setBlockedBookings(locationSpecificBookings); // Only show location-specific bookings in dialog
          
          // If all stock at THIS location is booked by location-specific bookings, show booking block dialog
          if (locationSpecificBookings.length > 0 && locationSpecificBooked >= locItem.antal) {
            setShowBookingBlockDialog(true);
            setProcessing(false);
            return;
          }
        } catch (error) {
          // No bookings or error - continue
          setCurrentStockForDelivery(locItem.antal);
          setBookedQuantityForDelivery(0);
          setBlockedBookings([]);
        }
        
        // Show delivery type dialog
        setPendingDeliveryData({
          ean: scannedProduct.ean,
          location: lagerplats.trim(),
          quantity: antal,
          product: scannedProduct
        });
        setShowDeliveryTypeDialog(true);
        setProcessing(false);
        return;
        
      } else {
        const scanData = {
          ean: scannedProduct.ean,
          lagerplats: lagerplats.trim(),
          antal: antal,
          is_stocktaking: inventoryMode
        };
        
        if (scannedProduct.isNew) {
          scanData.artikelnummer = scannedProduct.artikelnummer;
          scanData.tillverkare = scannedProduct.tillverkare;
          scanData.kategori = scannedProduct.kategori;
        }
        
        const response = await axios.post(`${API}/inventory/scan`, scanData);
        const { action, item, message } = response.data;
        
        setLastScanned({ action, item, message });
        const newScan = { action, item, timestamp: new Date().toISOString(), username: user?.display_name || user?.username || "" };
        setRecentScans(prev => [newScan, ...prev.slice(0, 2)]);
        
        // Save recent location for scan-in
        if (!inventoryMode) {
          addRecentLocation(lagerplats.trim());
        }
        
        if (inventoryMode) {
          // Show a clear "saved" confirmation with the NEW quantity at this location,
          // then re-fetch the live list from the DB so staff SEE the saved truth.
          const newQty = item?.antal ?? null;
          setLastInventoryUpdate({
            ean: scannedProduct.ean,
            artikelnummer: scannedProduct.artikelnummer || scannedProduct.ean,
            newQty,
            delta: antal,
          });
          toast.success(
            newQty != null
              ? `Sparat ✓ ${scannedProduct.artikelnummer || scannedProduct.ean}: ${newQty} st på ${lagerplats}`
              : `Sparat ✓ ${message}`,
            { icon: <CheckCircle className="h-4 w-4 text-green-500" /> }
          );
          refreshInventoryList(lagerplats.trim());
        } else if (action === "updated") {
          toast.success(message, { icon: <CheckCircle className="h-4 w-4 text-green-500" /> });
        } else if (action === "created") {
          toast.success(message, { icon: <Plus className="h-4 w-4 text-blue-500" /> });
        }
      }

      // Reset
      setEan("");
      setScannedProduct(null);
      setProductLocations([]);
      if (!inventoryMode) setLagerplats("");
      setAntal(1);
      setScanMode("in");
      fetchLocations();
      
      // Navigate back if came from product card
      if (returnTo) {
        navigate(returnTo);
        return;
      }
      
      // Focus back on EAN input after a short delay to ensure DOM is updated
      setTimeout(() => {
        eanInputRef.current?.focus();
      }, 100);
      
    } catch (error) {
      toast.error("Fel vid registrering: " + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(false);
    }
  };
  
  // Handler for when delivery type is selected (called from DeliveryTypeDialog)
  const handleDeliveryTypeSelect = async ({ type, orderNumber }) => {
    if (!pendingDeliveryData) return;
    
    setProcessing(true);
    setShowDeliveryTypeDialog(false);
    
    try {
      const response = await axios.post(`${API}/inventory/scan-out`, {
        ean: pendingDeliveryData.ean,
        location: pendingDeliveryData.location,
        quantity: pendingDeliveryData.quantity,
        delivery_type: type,
        order_reference: orderNumber
      });
      
      // Check if blocked
      if (response.data.blocked) {
        if (response.data.reason === "BLOCKED_ALL_BOOKED") {
          setBlockedBookings(response.data.bookings || []);
          setShowBookingBlockDialog(true);
        } else if (response.data.reason === "BLOCKED_BOOKING_MISMATCH") {
          // Show dialog with hint to enter correct order number
          setBlockedBookings(response.data.bookings || []);
          toast.error(response.data.message, { duration: 5000 });
          // Keep delivery dialog open so user can enter correct order number
          setShowDeliveryTypeDialog(true);
        }
        setProcessing(false);
        return;
      }
      
      const { item, message, delivery_type, order_reference, auto_cancelled_bookings, warning } = response.data;
      setLastScanned({ action: "scanned_out", item, message, delivery_type, order_reference });
      
      const deliveryLabel = type === "store_sale" ? "Butik" : "Runda";
      const newScan = { 
        action: "scanned_out", 
        item: { ...pendingDeliveryData.product, lagerplats: pendingDeliveryData.location, antal: pendingDeliveryData.quantity }, 
        timestamp: new Date().toISOString(),
        username: user?.display_name || user?.username || "",
        delivery_type: type,
        order_reference: orderNumber
      };
      setRecentScans(prev => [newScan, ...prev.slice(0, 2)]);
      
      // Show success message
      toast.success(`${pendingDeliveryData.quantity} st utskannade (${deliveryLabel})${orderNumber ? ` - ${orderNumber}` : ""}`, { 
        icon: <ArrowUpFromLine className="h-4 w-4 text-red-500" /> 
      });
      
      // Show info about auto-cancelled bookings
      if (auto_cancelled_bookings && auto_cancelled_bookings.length > 0) {
        const bookingRefs = auto_cancelled_bookings.map(b => b.comment).join(", ");
        toast.info(`Bokning avbokad automatiskt: ${bookingRefs}`, { duration: 4000 });
      }
      
      // Show warning about unspecified bookings if any
      if (warning) {
        toast.warning(warning, { duration: 5000 });
      }
      
      // Reset
      setEan("");
      setScannedProduct(null);
      setProductLocations([]);
      if (!inventoryMode) setLagerplats("");
      setAntal(1);
      setScanMode("in");
      setPendingDeliveryData(null);
      fetchLocations();
      
      // Navigate back if came from product card
      if (returnTo) {
        navigate(returnTo);
        return;
      }
      
      setTimeout(() => {
        eanInputRef.current?.focus();
      }, 100);
      
    } catch (error) {
      const errorDetail = error.response?.data?.detail || error.message;
      if (errorDetail.includes("BLOCKED_ZERO_STOCK")) {
        setShowZeroStockDialog(true);
      } else {
        toast.error("Fel vid utleverans: " + errorDetail);
      }
    } finally {
      setProcessing(false);
    }
  };
  
  // Handler for canceling a booking from the block dialog
  const handleCancelBooking = async (booking) => {
    try {
      await axios.delete(`${API}/inventory/bookings/${booking.id}`);
      toast.success("Bokning avbokad");
      
      // Refresh bookings
      const bookingsResponse = await axios.get(`${API}/inventory/bookings/by-ean/${scannedProduct?.ean || pendingDeliveryData?.ean}`);
      const bookings = bookingsResponse.data || [];
      
      // Get the current location for filtering
      const currentLocation = pendingDeliveryData?.location;
      
      // Filter to only location-specific bookings for THIS location
      const locationSpecificBookings = bookings.filter(b => b.location === currentLocation);
      const locationSpecificBooked = locationSpecificBookings.reduce((sum, b) => sum + (b.quantity || 1), 0);
      
      // Total for display
      const totalBooked = bookings.reduce((sum, b) => sum + (b.quantity || 1), 0);
      
      setBlockedBookings(locationSpecificBookings);
      setBookedQuantityForDelivery(totalBooked);
      
      // If no more location-specific bookings blocking, show delivery dialog
      if (locationSpecificBookings.length === 0 || locationSpecificBooked < currentStockForDelivery) {
        setShowBookingBlockDialog(false);
        if (pendingDeliveryData) {
          setShowDeliveryTypeDialog(true);
        }
      }
    } catch (error) {
      toast.error("Kunde inte avboka: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleFinishInventory = async () => {
    const location = inventoryLocation;
    setLoadingProducts(true);
    try {
      const response = await axios.get(`${API}/inventory/location/${encodeURIComponent(location)}`);
      setLocationProducts(response.data.items || []);
      setCompletedLocation(location);
      setInventoryCompleted(true);
    } catch (error) {
      setLocationProducts([]);
    } finally {
      setLoadingProducts(false);
    }
    
    setInventoryMode(false);
    setLagerplats("");
    
    // Notify global stocktaking context
    if (finishStocktaking) {
      finishStocktaking();
    }
    
    toast.success(`Inventering av ${location} slutförd!`);
    window.history.replaceState({}, '', '/scan');
  };
  
  const handleNewInventory = () => {
    setInventoryCompleted(false);
    setCompletedLocation("");
    setLocationProducts([]);
    navigate('/stocktaking');
  };

  const handleSaveNewProduct = () => {
    if (!newProduct.artikelnummer.trim()) {
      toast.error("Ange artikelnummer");
      return;
    }
    if (!newProduct.tillverkare.trim()) {
      toast.error("Välj tillverkare");
      return;
    }
    if (!newProduct.kategori.trim()) {
      toast.error("Välj kategori");
      return;
    }

    setScannedProduct({
      ean: newProduct.ean,
      artikelnummer: newProduct.artikelnummer,
      tillverkare: newProduct.tillverkare,
      kategori: newProduct.kategori,
      isNew: true,
    });
    setProductLocations([]);
    setShowNewProductDialog(false);
    toast.success("Produktinfo sparad - välj nu lagerplats och antal");
    locationInputRef.current?.focus();
  };

  const handleClearProduct = () => {
    setEan("");
    setScannedProduct(null);
    setProductLocations([]);
    setLagerplats("");
    setAntal(1);
    setScanMode("in");
    setTimeout(() => {
      eanInputRef.current?.focus();
    }, 50);
  };

  return {
    // State
    ean, setEan,
    lagerplats, setLagerplats,
    antal, setAntal,
    processing,
    lastScanned,
    locations,
    recentScans,
    recentLocations,
    inventoryMode,
    inventoryLocation,
    inventoryCompleted,
    completedLocation,
    locationProducts,
    loadingProducts,
    inventoryList,
    inventoryListLoading,
    lastInventoryUpdate,
    refreshInventoryList,
    scannedProduct,
    productLocations,
    lookingUp,
    showCameraScanner,
    cameraError,
    cameraLoading,
    scannerContainerId,
    showNewProductDialog, setShowNewProductDialog,
    newProduct, setNewProduct,
    categories,
    manufacturers,
    showScanActionDialog, setShowScanActionDialog,
    scanMode,
    pendingProduct,
    pendingLocations,
    showBookingDialog, setShowBookingDialog,
    productBookings,
    
    // Delivery dialog states (Fas 3)
    showDeliveryTypeDialog, setShowDeliveryTypeDialog,
    showZeroStockDialog, setShowZeroStockDialog,
    showBookingBlockDialog, setShowBookingBlockDialog,
    blockedBookings,
    currentStockForDelivery,
    bookedQuantityForDelivery,
    pendingDeliveryData,
    
    // Refs
    eanInputRef,
    locationInputRef,
    
    // Actions
    startCameraScanner,
    stopCameraScanner,
    handleEanLookup,
    handleScanActionChoice,
    handleCancelScanAction,
    handleMoveChoice,
    handleScanSubmit,
    handleFinishInventory,
    handleNewInventory,
    handleSaveNewProduct,
    handleClearProduct,
    handleBookingChoice,
    handleSkipBooking,
    
    // Delivery actions (Fas 3)
    handleDeliveryTypeSelect,
    handleCancelBooking,
  };
}
